import { useEffect, useRef, useState } from 'react'
import { jumpReplicaClock, setReplicaClock } from '../api/dev'
import { loadGameweekPhases } from '../storage'

/**
 * "Run gameweek" — the replica half of `Play live`.
 *
 * The mock implementation animates a bundled result set client-side and finalizes the gameweek
 * server-side in one jump. Against a replica that is doubly wrong: it animates `/api/results`,
 * which holds only **finished** fixtures, so an unplayed gameweek animates nothing while the
 * server half jumps straight to finalize — and the button reports success either way.
 *
 * So here the dev tool contributes **a clock speed and nothing else**:
 *
 * 1. save the admin's own predictions as they stand — no auto-fill, because the deadline job
 *    fills everyone when the derived deadline passes, and doing it here too is a second writer;
 * 2. jump to just before the gameweek's deadline;
 * 3. run at 60×, so a 90-minute match takes 90 seconds;
 * 4. freeze when the gameweek completes — detected from the derived phase, not from a timer.
 *
 * Everything visible in between comes from the real path: the scheduler polls, fixtures move
 * `scheduled → live → finished`, the phase derives, predictions fill, points move as fixtures
 * settle, and the reveal cascade renders it. **This hook writes no phase, prediction-fill or score
 * rows of its own.**
 */

/** A 90-minute match in 90 seconds: fast enough to watch, slow enough to see the cascade. */
export const RUN_SPEED = 60
/** How often the derived phase is re-read while a run is in flight. */
export const RUN_POLL_MS = 2_000
/**
 * A wall-clock bound on a run. A runaway clock left at 60× is the one genuinely annoying failure
 * mode here — it keeps burning through the season while nobody is watching — so a run that has
 * not completed by now freezes anyway.
 */
export const RUN_MAX_MS = 15 * 60_000
/** Land just before the deadline, so the lock itself is one of the things you watch happen. */
export const START_OFFSET_MINUTES = -5

export interface ReplicaGameweekRun {
  isRunning: boolean
  /** Starts a run; pressing it again while one is in flight freezes the clock instead. */
  run: (gameweek: number) => Promise<void>
  /** Freezes the replica clock and abandons any run in flight. */
  stop: () => void
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function useReplicaGameweekRun(
  options: { pollMs?: number; maxMs?: number; onToast?: (message: string) => void } = {}
): ReplicaGameweekRun {
  const { pollMs = RUN_POLL_MS, maxMs = RUN_MAX_MS, onToast } = options
  const [isRunning, setIsRunning] = useState(false)
  const runningRef = useRef(false)
  const cancelledRef = useRef(false)

  // Navigating away must not leave the replica running at 60× — the clock outlives this component.
  useEffect(
    () => () => {
      if (!runningRef.current) return
      cancelledRef.current = true
      void setReplicaClock({ speed: 0 })
    },
    []
  )

  const stop = () => {
    cancelledRef.current = true
  }

  const run = async (gameweek: number) => {
    if (runningRef.current) {
      stop()
      return
    }
    runningRef.current = true
    cancelledRef.current = false
    setIsRunning(true)
    try {
      // `gameweek-deadline` resolves server-side from the fixtures we hold, so it inherits
      // whatever a deadline means, without this file knowing about it.
      await jumpReplicaClock('gameweek-deadline', gameweek)
      await setReplicaClock({ advanceMinutes: START_OFFSET_MINUTES })
      await setReplicaClock({ speed: RUN_SPEED })

      const until = Date.now() + maxMs
      while (!cancelledRef.current && Date.now() < until) {
        // `resultsRevealed` is derived as "every fixture finished **or voided**", so a gameweek
        // with a postponed match completes rather than waiting forever on a match that will never
        // be played.
        const phases = await loadGameweekPhases()
        if (phases.find((phase) => phase.gameweek === gameweek)?.resultsRevealed) break
        await sleep(pollMs)
      }
    } catch (error) {
      onToast?.('Failed to run the gameweek against the replica clock.')
      console.error('Failed to run the gameweek', error)
    } finally {
      // Always, on every path: an exception mid-run must not leave the clock racing either.
      try {
        await setReplicaClock({ speed: 0 })
      } catch {
        // The freeze is best-effort; the run is over regardless.
      }
      runningRef.current = false
      setIsRunning(false)
    }
  }

  return { isRunning, run, stop }
}
